import { useEffect, useState } from "react";
import { Download, X, Smartphone } from "lucide-react";

/**
 * Prompt users to install the Adams app to their home screen.
 *
 * - Android/Chrome/Edge: uses the native `beforeinstallprompt` event so we
 *   can trigger the browser's install UI on tap.
 * - iOS Safari: no programmatic install API, so we show a small info card
 *   explaining the "Share → Add to Home Screen" flow.
 *
 * The prompt is dismissible; the dismissal is remembered for 14 days per
 * device so we don't nag daily.
 */
export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    // Already installed → don't show
    if (window.matchMedia?.("(display-mode: standalone)").matches) return;
    if (window.navigator.standalone) return;

    // Snooze check
    const snoozeUntil = Number(localStorage.getItem("install_prompt_snooze") || 0);
    if (snoozeUntil && Date.now() < snoozeUntil) return;

    // iOS detection
    const ua = window.navigator.userAgent || "";
    const isIOS = /iPhone|iPad|iPod/.test(ua);
    const isSafari = /^((?!chrome|android).)*safari/i.test(ua);
    if (isIOS && isSafari) {
      // Delay to avoid competing with initial render
      setTimeout(() => setIosHint(true), 3500);
      return;
    }

    const onBIP = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onBIP);
    return () => window.removeEventListener("beforeinstallprompt", onBIP);
  }, []);

  const snooze = () => {
    localStorage.setItem("install_prompt_snooze", String(Date.now() + 14 * 24 * 60 * 60 * 1000));
    setVisible(false);
    setIosHint(false);
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    try { await deferred.userChoice; } catch {}
    setDeferred(null);
    setVisible(false);
  };

  if (visible && deferred) {
    return (
      <div
        className="fixed bottom-20 md:bottom-3 left-3 right-3 sm:left-auto sm:right-4 sm:max-w-sm z-[60] rounded-xl bg-white border border-[#e2e8f0] shadow-2xl p-3 flex items-center gap-3"
        data-testid="install-prompt"
      >
        <div className="w-10 h-10 rounded-lg bg-[#dc2626]/10 text-[#dc2626] flex items-center justify-center flex-shrink-0">
          <Download size={18} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-bold text-[#0f172a]">Install Adams Quotation</div>
          <div className="text-[11.5px] text-[#64748b]">Faster launch, home-screen icon, no browser bar.</div>
        </div>
        <button
          onClick={install}
          className="px-3 h-9 rounded-lg bg-[#dc2626] hover:bg-[#b91c1c] text-white text-xs font-bold flex-shrink-0"
          data-testid="install-btn"
        >
          Install
        </button>
        <button
          onClick={snooze}
          className="p-1 text-[#94a3b8] hover:text-[#475569] flex-shrink-0"
          aria-label="Dismiss"
          data-testid="install-dismiss"
        >
          <X size={16} />
        </button>
      </div>
    );
  }

  if (iosHint) {
    return (
      <div
        className="fixed bottom-20 md:bottom-3 left-3 right-3 z-[60] rounded-xl bg-white border border-[#e2e8f0] shadow-2xl p-3"
        data-testid="install-prompt-ios"
      >
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#dc2626]/10 text-[#dc2626] flex items-center justify-center flex-shrink-0">
            <Smartphone size={18} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-bold text-[#0f172a]">Add Adams Quotation to Home Screen</div>
            <div className="text-[11.5px] text-[#64748b] mt-0.5">
              Tap <strong>Share</strong> in Safari, then <strong>Add to Home Screen</strong> for a one-tap launch.
            </div>
          </div>
          <button
            onClick={snooze}
            className="p-1 text-[#94a3b8] hover:text-[#475569] flex-shrink-0"
            aria-label="Dismiss"
            data-testid="install-dismiss-ios"
          >
            <X size={16} />
          </button>
        </div>
      </div>
    );
  }

  return null;
}
